// autopilot-doctor-orphan-checks.ts
//
// Phase 06g R2d (dogfood 6: `doctor` after SIGTERM printed nothing about the
// stuck ticket — D6/evidence/interrupt/doctor-after-sigterm.out). Two checks:
//   1. every `in_progress` ticket, by binding state — absent / expired /
//      valid + worker dead / valid + worker alive (pid named);
//   2. the machine lock when its pid is dead or reused — first line is the
//      exact `unlock` command;
//   3. (06h R2, dogfood 7 F-39) the journal lease `locks/journal.lock` whose
//      owner is dead (kill -9 skips `finally`) — same first line. Doctor only
//      reports; releasing stays with `unlock` (pid/lstart checked there).
// Warn only: doctor blocks `run` solely on RECOVERING (autopilot-doctor.ts).
import { hostname as osHostname } from "node:os";
import { join } from "node:path";
import type { AutopilotCheckResult } from "./autopilot-doctor-manifest-checks.js";
import { classifyJournalLeaseOwner } from "./journal-lease-owner-liveness.js";
import { readMachineLock } from "./mission-lock-file-io.js";
import { openMissionStoreReadOnly } from "./mission-store-read-only.js";
import {
  type ActiveWorkerLivenessDeps,
  defaultReadBindingLease,
  probeActiveWorkerLiveness,
} from "./orphaned-ticket-liveness-readers.js";
import { deadOrPidReused, realLivenessProbe } from "./process-liveness-probe.js";

export interface OrphanDoctorChecksInput extends ActiveWorkerLivenessDeps {
  controlRoot: string;
  missionWorkspace: string;
  missionId: string;
  machineLockPath: string;
  now?: () => Date;
}

async function inProgressTicketIds(controlRoot: string): Promise<string[]> {
  const ro = await openMissionStoreReadOnly(controlRoot);
  try {
    return Object.entries(ro.state.tickets)
      .filter(([, status]) => status === "in_progress")
      .map(([id]) => id);
  } finally {
    await ro.close();
  }
}

async function describeInProgress(
  input: OrphanDoctorChecksInput,
  ids: string[],
): Promise<AutopilotCheckResult> {
  const name = "Ticket in_progress / binding";
  const lease = await defaultReadBindingLease(input.missionWorkspace);
  const list = ids.join(", ");
  if (lease === undefined) {
    return {
      name,
      status: "warn",
      detail: `${list}: không có binding — \`run\` kế sẽ reclaim (supervisor_crash), archive rồi reset worktree.`,
    };
  }
  if ("corrupt" in lease) {
    return {
      name,
      status: "warn",
      detail: `${list}: binding không đọc được — KHÔNG tự reclaim, xem forensics.`,
    };
  }
  const now = (input.now ?? (() => new Date()))();
  const expiresAtMs = Date.parse(lease.expires_at);
  if (!Number.isFinite(expiresAtMs) || now.getTime() >= expiresAtMs) {
    return {
      name,
      status: "warn",
      detail: `${list}: binding lease đã hết hạn (${lease.expires_at}) — \`run\` kế sẽ reclaim.`,
    };
  }
  const probe = await probeActiveWorkerLiveness(input.controlRoot, {
    ...input,
    hostname: input.hostname ?? osHostname,
  });
  if (probe.state === "dead") {
    return {
      name,
      status: "warn",
      detail: `${list}: binding còn hạn tới ${lease.expires_at} nhưng worker pid ${probe.record.pid} và supervisor pid ${probe.record.supervisor_pid} đã chết — \`run\` kế sẽ reclaim ngay.`,
    };
  }
  if (probe.state === "alive") {
    return {
      name,
      status: "ok",
      detail: `${list}: worker pid ${probe.record.pid} còn sống (lease tới ${lease.expires_at}); muốn dừng: \`avatar autopilot unlock --mission ${input.missionId}\`.`,
    };
  }
  return {
    name,
    status: "warn",
    detail: `${list}: binding còn hạn tới ${lease.expires_at}, không xác minh được worker (không có record trên máy này) — đợi lease hoặc \`avatar autopilot unlock --mission ${input.missionId}\`.`,
  };
}

async function describeLocks(input: OrphanDoctorChecksInput): Promise<AutopilotCheckResult[]> {
  const liveness = input.liveness ?? realLivenessProbe;
  const lock = await readMachineLock(input.machineLockPath);
  const supervisorDead =
    lock !== undefined && (await deadOrPidReused(liveness, lock.pid, lock.processStartedAt));
  const unlock = `avatar autopilot unlock --mission ${input.missionId}`;
  const machine: AutopilotCheckResult =
    lock === undefined
      ? { name: "Machine lock", status: "ok", detail: "không có lock." }
      : supervisorDead
        ? {
            name: "Machine lock",
            status: "warn",
            detail: `avatar autopilot unlock --mission ${lock.missionId}\nLock ${input.machineLockPath} của mission ${lock.missionId}: pid ${lock.pid} đã chết hoặc bị tái dùng — \`run\` sẽ báo RECOVERING cho tới khi chạy lệnh trên.`,
          }
        : {
            name: "Machine lock",
            status: "ok",
            detail: `mission ${lock.missionId} đang chạy (pid ${lock.pid}).`,
          };
  // 06h R2: the lease `run` holds while writing the journal; `kill -9` leaves it behind.
  const lease = await classifyJournalLeaseOwner({
    leasePath: join(input.controlRoot, "locks", "journal.lock"),
    machineLock: lock,
    supervisorDead,
    liveness,
  });
  const journal: AutopilotCheckResult =
    lease.state === "absent"
      ? { name: "Journal lease", status: "ok", detail: "không có lease." }
      : lease.state === "owner_dead"
        ? {
            name: "Journal lease",
            status: "warn",
            detail: `${unlock}\nruntime/locks/journal.lock còn của pid ${lease.pid} đã chết (supervisor bị kill -9) — \`run\` sẽ báo RECOVERING cho tới khi chạy lệnh trên.`,
          }
        : {
            name: "Journal lease",
            status: "ok",
            detail: `run đang giữ journal (pid ${lease.pid ?? "?"}).`,
          };
  return [machine, journal];
}

export async function runOrphanDoctorChecks(
  input: OrphanDoctorChecksInput,
): Promise<AutopilotCheckResult[]> {
  const ids = await inProgressTicketIds(input.controlRoot);
  const ticketCheck: AutopilotCheckResult =
    ids.length === 0
      ? {
          name: "Ticket in_progress / binding",
          status: "ok",
          detail: "không có ticket in_progress.",
        }
      : await describeInProgress(input, ids);
  return [ticketCheck, ...(await describeLocks(input))];
}
